import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
	CustomButton,
	CustomDialog,
	CustomIconButton,
	icon,
	IconComponent,
	SidePanel,
	SidePanelContent,
	CustomTooltip,
} from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import { useApi } from "../../../../../../common/hooks";
import { CommitmentDefinitionService } from "../../../../../../api";
import styles from "./ThemeManagementPanel.module.css";
import ThemeFormDialog from "./ThemeFormDialog";

export default function ThemeManagementDialog({ open, themes, onClose, onRefresh }) {
	const [localThemes, setLocalThemes] = useState([]);
	const [deleteConfirmTheme, setDeleteConfirmTheme] = useState(null);
	const [archiveConfirmTheme, setArchiveConfirmTheme] = useState(null);
	const [hasChanges, setHasChanges] = useState(false);
	const [themeFormOpen, setThemeFormOpen] = useState(false);
	const [editingTheme, setEditingTheme] = useState(null);

	const { call: createTheme } = useApi(CommitmentDefinitionService.createTheme);
	const { call: updateTheme } = useApi(CommitmentDefinitionService.updateTheme);
	const { call: deleteTheme } = useApi(CommitmentDefinitionService.deleteTheme);
	const { call: updateThemeOrder } = useApi(CommitmentDefinitionService.updateThemeOrder);

	useEffect(() => {
		setLocalThemes([...themes].sort((a, b) => (a.rank || 0) - (b.rank || 0)));
	}, [themes]);

	const handleAddTheme = () => {
		setEditingTheme(null);
		setThemeFormOpen(true);
	};

	const handleStartEdit = (theme) => {
		if (theme.isDeprecated) {
			return;
		}
		setEditingTheme(theme);
		setThemeFormOpen(true);
	};

	const handleThemeFormClose = () => {
		setThemeFormOpen(false);
		setEditingTheme(null);
	};

	const handleThemeFormSubmit = (formData) => {
		if (editingTheme) {
			updateTheme(
				{ id: editingTheme.id },
				{ name: formData.name, description: formData.description, isDeprecated: editingTheme.isDeprecated }
			)
				.then(() => {
					setHasChanges(true);
					setThemeFormOpen(false);
					setEditingTheme(null);
					if (onRefresh && typeof onRefresh === "function") {
						onRefresh();
					}
				})
				.catch(console.error);
		} else {
			createTheme({ name: formData.name, description: formData.description, isDeprecated: false })
				.then(() => {
					setHasChanges(true);
					setThemeFormOpen(false);
					if (onRefresh && typeof onRefresh === "function") {
						onRefresh();
					}
				})
				.catch(console.error);
		}
	};

	const handleArchiveClick = (theme) => {
		setArchiveConfirmTheme(theme);
	};

	const handleConfirmArchive = () => {
		if (!archiveConfirmTheme) {
			return;
		}
		updateTheme(
			{ id: archiveConfirmTheme.id },
			{ name: archiveConfirmTheme.name, description: archiveConfirmTheme.description, isDeprecated: true }
		)
			.then(() => {
				setHasChanges(true);
				setArchiveConfirmTheme(null);
				if (onRefresh && typeof onRefresh === "function") {
					onRefresh();
				}
			})
			.catch(console.error);
	};

	const handleDeleteClick = (theme) => {
		setDeleteConfirmTheme(theme);
	};

	const handleConfirmDelete = () => {
		if (!deleteConfirmTheme) {
			return;
		}
		deleteTheme({ id: deleteConfirmTheme.id })
			.then(() => {
				setHasChanges(true);
				setDeleteConfirmTheme(null);
				if (onRefresh && typeof onRefresh === "function") {
					onRefresh();
				}
			})
			.catch(console.error);
	};

	const handleDragEnd = (result) => {
		if (!result.destination) {
			return;
		}

		const sourceIndex = result.source.index;
		const destIndex = result.destination.index;

		if (sourceIndex === destIndex) {
			return;
		}

		const reordered = Array.from(localThemes);
		const [removed] = reordered.splice(sourceIndex, 1);
		reordered.splice(destIndex, 0, removed);

		setLocalThemes(reordered);

		const movedItem = removed;
		const previousItem = destIndex > 0 ? reordered[destIndex - 1] : null;
		const nextItem = destIndex < reordered.length - 1 ? reordered[destIndex + 1] : null;

		const payload = {
			itemId: movedItem.id,
			previousItemId: previousItem?.id || null,
			nextItemId: nextItem?.id || null,
		};

		updateThemeOrder(payload)
			.then(() => {
				setHasChanges(true);
				if (onRefresh && typeof onRefresh === "function") {
					onRefresh();
				}
			})
			.catch(console.error);
	};

	const canDeleteTheme = (theme) => theme.isDeprecated && !theme.hasCommitments;

	const handleSave = () => {
		setHasChanges(false);
		onClose();
	};

	return (
		<>
			<SidePanel open={open} size={45} onClose={onClose}>
				<SidePanelContent
					secondaryActions={
						<CustomButton color="primary" disabled={!hasChanges} variant="contained" onClick={handleSave}>
							{translate("common:btn.save")}
						</CustomButton>
					}
					title={translate("dsi.commitment-definition.manage-themes")}
					onClose={onClose}
				>
					<div className={styles.container}>
						<div className={styles.subHeader}>
							<span className={styles.subHeaderTitle}>
								{translate("dsi.commitment-definition.themes.title")}
							</span>
							<CustomButton
								color="primary"
								startIcon={icon.faPlus}
								variant="outlined"
								onClick={handleAddTheme}
							>
								{translate("dsi.commitment-definition.theme.new")}
							</CustomButton>
						</div>
						<div className={styles.sortedBy}>
							{translate("dsi.commitment-definition.themes.sorted-by-rank")}
						</div>
						<DragDropContext onDragEnd={handleDragEnd}>
							<Droppable droppableId="themes">
								{(provided) => (
									<div
										ref={provided.innerRef}
										className={styles.themeList}
										{...provided.droppableProps}
									>
										{localThemes.map((theme, index) => (
											<Draggable
												key={theme.id}
												disabled={theme.isDeprecated}
												draggableId={String(theme.id)}
												index={index}
												isDragDisabled={theme.isDeprecated}
											>
												{(dragProvided, snapshot) => {
													const child = (
														<div
															ref={dragProvided.innerRef}
															className={`${styles.themeRow} ${
																snapshot.isDragging ? styles.dragging : ""
															} ${theme.isDeprecated ? styles.deprecated : ""}`}
															{...dragProvided.draggableProps}
															style={dragProvided.draggableProps.style}
														>
															<span
																className={`${styles.dragHandle} ${
																	theme.isDeprecated ? styles.disabled : ""
																}`}
																{...dragProvided.dragHandleProps}
															>
																<IconComponent
																	className={styles.dragIcon}
																	icon={icon.faGripLines}
																/>
															</span>
															<CustomTooltip showWhenOverflow title={theme.name}>
																<span
																	className={`${styles.themeName} ${
																		theme.isDeprecated ? styles.deprecatedText : ""
																	}`}
																	role="button"
																	tabIndex={theme.isDeprecated ? -1 : 0}
																	onClick={() => handleStartEdit(theme)}
																	onKeyPress={(e) => {
																		if (e.key === "Enter") {
																			handleStartEdit(theme);
																		}
																	}}
																>
																	{theme.name}
																	{theme.isDeprecated && (
																		<span className={styles.deprecatedBadge}>
																			(
																			{translate(
																				"dsi.commitment-definition.theme.deprecated"
																			)}
																			)
																		</span>
																	)}
																</span>
															</CustomTooltip>
															<div className={styles.themeActions}>
																{!theme.isDeprecated && (
																	<>
																		<CustomIconButton
																			icon={icon.faPenToSquare}
																			size="sm"
																			tooltip={translate("common:btn.edit")}
																			variant="outlined"
																			onClick={() => handleStartEdit(theme)}
																		/>
																		<CustomIconButton
																			icon={icon.faBoxArchive}
																			size="sm"
																			tooltip={translate(
																				"dsi.commitment-definition.theme.archive"
																			)}
																			variant="outlined"
																			onClick={() => handleArchiveClick(theme)}
																		/>
																	</>
																)}
																{canDeleteTheme(theme) && (
																	<CustomIconButton
																		icon={icon.faTrash}
																		size="sm"
																		tooltip={translate("common:btn.delete")}
																		variant="outlined"
																		onClick={() => handleDeleteClick(theme)}
																	/>
																)}
															</div>
														</div>
													);
													if (snapshot.isDragging) {
														return ReactDOM.createPortal(child, document.body);
													}
													return child;
												}}
											</Draggable>
										))}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</SidePanelContent>
			</SidePanel>
			<ThemeFormDialog
				open={themeFormOpen}
				theme={editingTheme}
				onClose={handleThemeFormClose}
				onSubmit={handleThemeFormSubmit}
			/>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={!!deleteConfirmTheme}
				submitColor="destructive"
				submitLabel={translate("common:btn.confirm")}
				subTitle={translate("dsi.commitment-definition.theme.delete-confirmation-text")}
				title={translate("dsi.commitment-definition.theme.delete-confirmation")}
				onClose={() => setDeleteConfirmTheme(null)}
				onSubmit={handleConfirmDelete}
			/>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={!!archiveConfirmTheme}
				submitLabel={translate("common:btn.confirm")}
				subTitle={translate("dsi.commitment-definition.theme.archive-confirmation-text")}
				title={translate("dsi.commitment-definition.theme.archive-confirmation")}
				onClose={() => setArchiveConfirmTheme(null)}
				onSubmit={handleConfirmArchive}
			/>
		</>
	);
}

ThemeManagementDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	themes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			name: PropTypes.string,
			description: PropTypes.string,
			isDeprecated: PropTypes.bool,
			rank: PropTypes.number,
			hasCommitments: PropTypes.bool,
		})
	),
	onClose: PropTypes.func.isRequired,
	onRefresh: PropTypes.func.isRequired,
};

ThemeManagementDialog.defaultProps = {
	themes: [],
};
